import {useEffect, useSyncExternalStore} from 'react';

import {useAccountContext} from '../account/AccountProvider';
import {
  applyColorScheme,
  parseColorMode,
  resolveIsDarkMode,
  THEME_STORAGE_KEY,
} from './colorScheme';

const MEDIA_QUERY = '(prefers-color-scheme: dark)';

function getMediaQuery(): MediaQueryList | null {
  return typeof window.matchMedia === 'function'
    ? window.matchMedia(MEDIA_QUERY)
    : null;
}

function getSystemPrefersDark(): boolean {
  return getMediaQuery()?.matches ?? false;
}

function subscribeToSystemPreference(onChange: () => void): () => void {
  const mediaQuery = getMediaQuery();
  if (mediaQuery == null) {
    return () => {};
  }

  mediaQuery.addEventListener('change', onChange);
  return () => mediaQuery.removeEventListener('change', onChange);
}

function getStoredTheme(): string | null {
  try {
    return localStorage.getItem(THEME_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function useColorScheme(): void {
  const {viewer} = useAccountContext();
  const systemPrefersDark = useSyncExternalStore(
    subscribeToSystemPreference,
    getSystemPrefersDark,
    () => false
  );
  const isDarkMode = resolveIsDarkMode(
    parseColorMode(viewer?.userInfo),
    getStoredTheme(),
    systemPrefersDark
  );

  useEffect(() => {
    applyColorScheme(isDarkMode);
  }, [isDarkMode]);
}

export const ColorSchemeSync = () => {
  useColorScheme();
  return null;
};
